import { useEffect, useRef, useState } from "react";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import { authFetch } from "../../../api";
import AddTaskModal from "./AddTaskModal";
import { useLocation } from "react-router-dom";
import CustomSnackbar from "../../../newTestPreo/components/snackbar/Snackbar";

const SAP_Task_Progress = () => {
  const baseUrl = process.env.REACT_APP_API_ENDPOINT;
  const userId = localStorage.getItem("userId");
  const [activeClass, setActiveClass] = useState("All Classes");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("Sort By Quarter");
  const [tasksData, setTasksData] = useState([]);
  const [summary, setSummary] = useState({});
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);
  const activeIndexRef = useRef(null);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [otherTasks, setOtherTasks] = useState([]);
  const [openOtherTasks, setOpenOtherTasks] = useState(false);
  const [openAddTaskModal, setOpenAddTaskModal] = useState(false);
  const location = useLocation();
  const { classID, stream } = location.state || {};
  const [activities, setActivities] = useState([]);
  const [activeQuarter, setActiveQuarter] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [enrolledClass, setEnrolledClass] = useState(null);
  const [classOptions, setClassOptions] = useState([]);

  const handleUploadClick = (index) => {
    activeIndexRef.current = index;
    fileInputRef.current.click();
  };

  const options = ["All Quarters", "Q1", "Q2", "Q3", "Q4"];

  const handleSelect = (option) => {
    setSelected(option);
    setOpen(false);

    // 🔥 Refetch with quarter filter
    fetchTasks(1, activeClass, option);
  };

  // const classes = [
  //   "All Classes",
  //   "Class 9",
  //   "Class 10",
  //   "Class 11",
  //   "Class 12",
  //   "Other Tasks",
  // ];
  const classes = [
    "All Classes",
    ...classOptions.map((cls) => cls.label),
    "Other Tasks",
  ];

  const filteredTasks =
    activeClass === "All Classes"
      ? tasksData
      : activeClass === "Other Tasks"
        ? otherTasks
        : tasksData.filter((t) => t.class === activeClass);

  const total = summary.total_tasks || 0;
  const completed = summary.completed_tasks || 0;
  const pending = summary.pending_tasks || 0;
  const completionRate = Math.round(summary.completion_rate || 0);

  const processedTasks = filteredTasks
    // ✅ FILTER by quarter
    .filter((task) => {
      if (!selected || selected === "Sort By Quarter") return true;

      return task.quarter === selected;
    })
    // ✅ SORT always ascending (optional but nice)
    .sort((a, b) => {
      const getQ = (q) => Number(q?.replace("Q", "")) || 0;
      return getQ(a.quarter) - getQ(b.quarter);
    });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const task = filteredTasks[activeIndexRef.current];
    if (!task) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      let response;

      if (activeClass === "Other Tasks") {
        response = await authFetch(
          `timeline/upload-student-timeline-other-task?platform=1&id=${task.task_id}`,
          {
            method: "POST",
            body: formData,
          },
        );
      } else {
        formData.append("user_id", userId);
        formData.append("task_id", task.task_id);

        response = await authFetch("timeline/upload-student-tasks", {
          method: "POST",
          body: formData,
        });
      }

      const result = await response.json();

      if (result.success) {
        setUploadStatus("success");

        setSnackbarMessage(
          activeClass === "Other Tasks"
            ? "Task file uploaded successfully!"
            : "Task submitted successfully!",
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        if (activeClass === "Other Tasks") {
          fetchOtherTasks();
        } else {
          fetchTasks();
        }
      } else {
        setUploadStatus("error");

        setSnackbarMessage(result.message || "Upload failed");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Upload error:", err);

      setUploadStatus("error");
      setSnackbarMessage("Something went wrong while uploading");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    e.target.value = "";
  };

  //API CALLS
  const fetchTasks = async (
    pageNumber = 1,
    selectedClass = activeClass,
    selectedQuarter = selected,
  ) => {
    try {
      let body = {
        user_id: userId,
      };

      const getClassId = (classLabel) => {
        const found = classOptions.find((c) => c.label === classLabel);
        return found ? found.value : null;
      };

      // ✅ Class filter
      if (selectedClass !== "All Classes" && selectedClass !== "Other Tasks") {
        const classId = getClassId(selectedClass);
        if (classId) body.class_id = classId;
      }

      // ✅ Quarter filter (IMPORTANT)
      if (selectedQuarter && selectedQuarter !== "All Quarters") {
        body.quarter = Number(selectedQuarter.replace("Q", ""));
      }

      const response = await authFetch(
        `timeline/show-student-task-progress?page=${pageNumber}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      const result = await response.json();

      if (result.success) {
        const tasks = result.data.task_records.map((task) => ({
          task_id: task.task_id,
          class: `Class ${task.class_id + 8}`,
          quarter: `Q${task.quarter}`,
          task: task.task_name,
          status: task.status,
          document: task.document,
          uploaded_task: task.uploaded_task,
        }));

        setTasksData(tasks);
        setSummary(result.data.summary);
        setPagination(result.data.pagination);
        setPage(result.data.pagination.current_page);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const checkClass = async () => {
    try {
      const res = await authFetch("timeline/show-student-enrolled-class", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const result = await res.json();

      const data = result.data;

      setEnrolledClass(data.student_enrolled_for);

      // 🔥 Map API data to dropdown format
      const mappedClasses = data.show_class_ids.map((id, index) => ({
        value: id,
        label: data.show_class_names[index],
      }));

      setClassOptions(mappedClasses);
    } catch (err) {
      console.log(err);
    }
  };

  const handleClassChange = (c) => {
    setActiveClass(c);
    setPage(1);

    if (c === "Other Tasks") {
      fetchOtherTasks();
    } else {
      fetchTasks(1, c, selected);
    }
  };

  const fetchOtherTasks = async () => {
    try {
      const response = await authFetch(
        "timeline/show-student-timeline-other-tasks",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: userId, is_active: 1 }),
        },
      );

      const result = await response.json();

      if (result.success) {
        const tasks = result.data.map((task, index) => ({
          task_id: task.id || index,
          class: "Other",
          quarter: "-",
          task: task.task_title,
          status: task.uploaded_path ? "Completed" : "Pending",
          document: task.uploaded_path,
          uploaded_task: task.uploaded_path,
        }));

        setOtherTasks(tasks);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const fetchData = async () => {
    try {
      const response = await authFetch("timeline/show-customized-data", {
        method: "POST",
        body: JSON.stringify({
          class_id: Number(classID),
          stream: Number(stream),
          user_id: userId,
        }),
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();
      if (result?.success) {
        setActivities(result.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);

    if (activeClass === "Other Tasks") {
      fetchOtherTasks(newPage);
    } else {
      fetchTasks(newPage, activeClass, selected);
    }
  };

  useEffect(() => {
    checkClass();
    fetchTasks(1, activeClass);
    fetchOtherTasks();
    fetchData();
  }, []);

  return (
    <div className=" min-h-screen">
      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
      <AddTaskModal
        openOtherTasks={openAddTaskModal}
        onClose={() => setOpenAddTaskModal(false)}
        activeQuarter={activeQuarter}
        activeClass={activeClass}
        refreshData={fetchData}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
      />
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,.mp4"
        className="hidden"
        onChange={handleFileChange}
      />
      {/* Header */}
      <div className="relative font-inter bg-gradient-to-r from-[#5F6EB4] to-[#C059C0] rounded-xl p-6 text-white mb-8">
        <h1 className="text-2xl font-bold">Student Task Progress</h1>
        <p className="mt-2 text-sm opacity-90">
          Track and manage your skill-building assignments across all academic
          classes.
        </p>

        {/* Button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={() => {
              setOpenOtherTasks(false);
              setOpenAddTaskModal(true);
            }}
            className="px-4 py-2 text-sm rounded-lg bg-white/20 hover:bg-white/30 transition"
          >
            Add Other Task
          </button>
        </div>
      </div>
      {/* Class Tabs */}
      <div className="flex gap-3 mb-6">
        {classes.map((c) => (
          <button
            key={c}
            onClick={() => handleClassChange(c)}
            className={`px-5 py-2 rounded-xl text-sm font-medium
            ${
              activeClass === c ? "bg-[#572575] text-white " : "bg-white border"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        <Card
          title="Total Tasks"
          value={total}
          imgsrc={"/taskProgress/totalTasks.png"}
        />

        <Card
          title="Completed Tasks"
          value={completed}
          imgsrc={"/taskProgress/completedTasks.png"}
        />

        <Card
          title="Pending Tasks"
          value={pending}
          imgsrc={"/taskProgress/pendingTasks.png"}
        />

        <div className="bg-white p-5 rounded-xl shadow">
          <img
            src="/taskProgress/completionRate.png"
            alt="CR"
            className="h-10 w-10 mb-2"
          />
          <p className="text-gray-500 text-sm">Completion Rate</p>
          <h2 className="text-2xl font-bold">{completionRate}%</h2>

          <div className="w-full bg-gray-200 h-2 rounded mt-3">
            <div
              className="bg-[#572575] h-2 rounded"
              style={{ width: `${completionRate}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold mb-4">Task Records</h2>
          <div className="relative inline-block">
            {/* Button */}
            {/* <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-3 px-4 py-2 border border-gray-300 rounded-xl bg-white hover:bg-gray-50 transition"
            >
              <ArrowUpDown size={13} className="text-gray-600" />
              <span className="text-gray-700 font-medium text-sm">
                {selected}
              </span>
              <ChevronDown size={18} className="text-gray-600" />
            </button> */}

            {/* Dropdown */}
            {open && (
              <div className="absolute mt-2 w-40 bg-white border border-gray-200 rounded-xl shadow-lg">
                {options.map((option) => (
                  <div
                    key={option}
                    onClick={() => handleSelect(option)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="text-gray-500 border-b">
            <tr>
              <th className="py-3 text-left">Class</th>
              <th className="text-left">Quarter</th>
              <th className="text-left">Task Name</th>
              <th className="text-left">Status</th>
              <th className="text-left">Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredTasks.map((task, index) => (
              <tr key={index} className="border-b">
                <td className="py-4">
                  <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs">
                    {task.class}
                  </span>
                </td>

                <td>{task.quarter}</td>

                <td>{task.task}</td>

                <td>
                  {task.status === "Completed" ? (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs">
                      Completed
                    </span>
                  ) : (
                    <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs">
                      Pending
                    </span>
                  )}
                </td>

                <td className="flex gap-2 my-4">
                  {task.document ? (
                    <>
                      <div className="flex items-center gap-2 ">
                        <button
                          onClick={() =>
                            window.open(
                              `${baseUrl.replace("/api/student", "")}${task.uploaded_task}`,
                              "_blank",
                            )
                          }
                          className="text-purple-700 bg-[#FAF5FF] text-[#572575] text-sm px-3 py-1 rounded-md"
                        >
                          View
                        </button>

                        <button
                          onClick={() => handleUploadClick(index)}
                          className="text-gray-500 text-sm px-3 py-1 rounded-md border"
                        >
                          Replace
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => handleUploadClick(index)}
                      className="bg-[#572575] text-white px-3 py-1 rounded-md text-xs"
                    >
                      Upload
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center justify-between mt-6">
          {/* Previous */}
          <button
            disabled={!pagination.has_previous_page}
            onClick={() => handlePageChange(page - 1)}
            className={`text-sm ${
              pagination.has_previous_page
                ? "text-gray-600 hover:text-black"
                : "text-gray-300 cursor-not-allowed"
            }`}
          >
            Previous
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-3">
            {Array.from({ length: pagination.total_pages || 0 }, (_, i) => {
              const pageNumber = i + 1;

              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`w-8 h-8 rounded-md text-sm flex items-center justify-center
          ${
            page === pageNumber
              ? "bg-purple-200 text-purple-800 font-semibold"
              : "text-gray-700 hover:bg-gray-100"
          }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          {/* Next */}
          <button
            disabled={!pagination.has_next_page}
            onClick={() => handlePageChange(page + 1)}
            className={`text-sm ${
              pagination.has_next_page
                ? "text-gray-600 hover:text-black"
                : "text-gray-300 cursor-not-allowed"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SAP_Task_Progress;

function Card({ title, value, imgsrc }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow">
      <img className="h-10 w-10 mb-4" src={imgsrc} alt={title} />
      <p className="text-gray-500 text-sm">{title}</p>
      <h2 className="text-2xl font-bold">{value}</h2>
    </div>
  );
}

const StatusModal = ({ type, title, description, onClose }) => {
  const isSuccess = type === "success";

  return (
    <div className="fixed inset-0 z-[1100] bg-black/5 flex items-center justify-center font-inter ">
      <div className="relative bg-white rounded-3xl w-[420px] p-8 text-center shadow-2xl">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          {isSuccess ? (
            <img src="/congo.png" alt="!congo" className="h-13 w-auto" />
          ) : (
            <img src="/!.png" alt="!.png" className="h-13 w-auto" />
          )}
        </div>

        {/* Text */}
        <h3 className="text-2xl font-bold text-black mb-2">{title}</h3>
        <p className="text-[#313439] text-md mb-6">{description}</p>

        {!isSuccess && (
          <button
            onClick={onClose}
            className="px-8 py-3 rounded-xl font-semibold text-sm text-white bg-[#4B1D6B] hover:bg-purple-800"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};
